import { useContext } from "react";
import { QueryClient, QueryClientContext } from "@tanstack/react-query";

import { FRONT_PERF_AUDIT_ENABLED, logFrontEvent, serializeQueryKey } from "./perfAudit";

let sharedQueryClient: QueryClient | null = null;

export function getQueryClient() {
  if (!sharedQueryClient) {
    sharedQueryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60_000,
          gcTime: 10 * 60_000,
          retry: 1,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    });
    instrumentQueryClient(sharedQueryClient);
  }
  return sharedQueryClient;
}

export function useAppQueryClient() {
  const contextClient = useContext(QueryClientContext);
  return contextClient ?? getQueryClient();
}

function instrumentQueryClient(queryClient: QueryClient) {
  if (!FRONT_PERF_AUDIT_ENABLED) return;

  const target = queryClient as QueryClient & { __perfInstrumented?: boolean };
  if (target.__perfInstrumented) return;
  target.__perfInstrumented = true;

  const originalInvalidateQueries = queryClient.invalidateQueries.bind(queryClient);
  queryClient.invalidateQueries = (async (filters, options) => {
    const startedAt = performance.now();
    logFrontEvent("QUERY_INVALIDATE_START", {
      queryKey: serializeQueryKey(filters?.queryKey),
      exact: filters?.exact ?? undefined,
    });

    try {
      const result = await originalInvalidateQueries(filters, options);
      logFrontEvent("QUERY_INVALIDATE_END", {
        queryKey: serializeQueryKey(filters?.queryKey),
        exact: filters?.exact ?? undefined,
        durationMs: roundDuration(performance.now() - startedAt),
        ok: true,
      });
      return result;
    } catch (error) {
      logFrontEvent("QUERY_INVALIDATE_END", {
        queryKey: serializeQueryKey(filters?.queryKey),
        exact: filters?.exact ?? undefined,
        durationMs: roundDuration(performance.now() - startedAt),
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }) as QueryClient["invalidateQueries"];

  const originalRefetchQueries = queryClient.refetchQueries.bind(queryClient);
  queryClient.refetchQueries = (async (filters, options) => {
    const startedAt = performance.now();
    logFrontEvent("QUERY_REFETCH_START", {
      queryKey: serializeQueryKey(filters?.queryKey),
      exact: filters?.exact ?? undefined,
    });

    try {
      const result = await originalRefetchQueries(filters, options);
      logFrontEvent("QUERY_REFETCH_END", {
        queryKey: serializeQueryKey(filters?.queryKey),
        exact: filters?.exact ?? undefined,
        durationMs: roundDuration(performance.now() - startedAt),
        ok: true,
      });
      return result;
    } catch (error) {
      logFrontEvent("QUERY_REFETCH_END", {
        queryKey: serializeQueryKey(filters?.queryKey),
        exact: filters?.exact ?? undefined,
        durationMs: roundDuration(performance.now() - startedAt),
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }) as QueryClient["refetchQueries"];
}

function roundDuration(value: number) {
  return Number(value.toFixed(2));
}
